import { useEffect, useRef } from 'react';
import type { Race } from '../../types';
import { formatDistance, sortDistances } from '../../utils/distance';
import { getDday } from '../../utils/dday';
import { formatKorDate, parseDate } from '../../utils/dateRange';
import { STATUS_LABELS } from '../../utils/raceLabels';
import { BookmarkButton } from '../../races/BookmarkButton';
import { RegistrationAction } from '../../races/RegistrationAction';
import './RaceInfoPanel.css';

interface RaceInfoPanelProps {
  race: Race;
  onClose: () => void;
}

export function RaceInfoPanel({ race, onClose }: RaceInfoPanelProps) {
  const dday = getDday(race);
  const sorted = sortDistances(race.distances);
  const panelRef = useRef<HTMLDivElement>(null);

  // ESC 로 닫기 — 모바일 뒤로가기·키보드 사용자 대응
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  useEffect(() => {
    panelRef.current?.focus({ preventScroll: true });
  }, [race.id]);

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('ko-KR', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const regStart = race.registration?.startDate;
  const regEnd = race.registration?.endDate;
  const regMissing = !parseDate(regStart) && !parseDate(regEnd);
  const cityLabel = race.city ?? race.region;
  const fees = race.fees ?? [];
  const website = race.website ?? race.registrationUrl ?? race.sourceUrl;

  return (
    <div
      ref={panelRef}
      className={`race-info-panel status-${race.status}`}
      tabIndex={-1}
      role="region"
      aria-label={`${race.nameLocal || race.name} 상세`}
    >
      <button className="race-info-close" onClick={onClose} aria-label="닫기">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>

      <div className="race-info-hero">
        <div className="race-info-hero-meta eyebrow">{dday.caption}</div>
        <div className="race-info-hero-dday display">{dday.label}</div>
        <span className={`race-info-status-badge status-${race.status}`}>
          {STATUS_LABELS[race.status]}
        </span>
      </div>

      <div className="race-info-title-block">
        <div className="race-info-title-row">
          <h2 className="race-info-title">{race.nameLocal || race.name}</h2>
          <BookmarkButton raceId={race.id} raceName={race.nameLocal || race.name} />
        </div>
        {race.nameLocal && race.name !== race.nameLocal && (
          <p className="race-info-subtitle">{race.name}</p>
        )}
      </div>

      <div className="race-info-distances-row">
        {sorted.map((d, i) => (
          <span
            key={d}
            className={`race-info-distance display ${i === sorted.length - 1 ? 'primary' : ''}`}
          >
            {formatDistance(d)}
          </span>
        ))}
      </div>

      <div className="race-info-content">
        <div className="race-info-row">
          <div className="race-info-label eyebrow">대회일</div>
          <div className="race-info-value">{formatKorDate(race.date, 'full')}</div>
        </div>

        <div className="race-info-row">
          <div className="race-info-label eyebrow">장소</div>
          <div className="race-info-value">
            {cityLabel}
            {race.venue && <span className="race-info-value-sub"> · {race.venue}</span>}
          </div>
        </div>

        <div className="race-info-row">
          <div className="race-info-label eyebrow">접수 기간</div>
          <div className="race-info-value">
            {regMissing ? (
              <span className="race-info-value-sub">접수 일정 미공개</span>
            ) : (
              <>
                {regStart && formatKorDate(regStart, 'numeric')}
                <span className="race-info-arrow"> → </span>
                {regEnd && formatKorDate(regEnd, 'numeric')}
              </>
            )}
          </div>
        </div>

        {fees.length > 0 && (
          <div className="race-info-fees-block">
            <div className="race-info-label eyebrow">참가비</div>
            <div className="race-info-fees">
              {fees.map((fee) => (
                <div key={fee.distance} className="race-info-fee">
                  <span className="fee-distance display">{fee.distance}</span>
                  <span className="fee-amount">{formatCurrency(fee.amount, fee.currency)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="race-info-footer">
        <RegistrationAction race={race} />
        <a
          href={website}
          target="_blank"
          rel="noopener noreferrer"
          className="race-info-cta"
        >
          <span className="race-info-cta-label">대회 사이트로</span>
          <span className="race-info-cta-arrow" aria-hidden="true">→</span>
        </a>
      </div>
    </div>
  );
}
